import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { SvgIconComponent } from "@mui/icons-material";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloudIcon from "@mui/icons-material/Cloud";
import AssessmentIcon from "@mui/icons-material/Assessment";
import StorageIcon from "@mui/icons-material/Storage";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const StyledBadge = styled("span")(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.primary.dark
      : theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "10px",
  padding: "3px 8px",
  fontSize: "0.75rem",
  fontWeight: "bold",
  boxShadow:
    theme.palette.mode === "dark" ? "0 0 8px rgba(0, 0, 0, 0.5)" : "none",
  border:
    theme.palette.mode === "dark"
      ? `1px solid ${theme.palette.mode === "dark" ? "rgba(60, 90, 180, 0.5)" : theme.palette.primary.light}`
      : "none",
}));

interface MenuItem {
  title: string;
  path?: string;
  icon?: React.ReactNode;
}

interface DrawerProps {
  drawerWidth: number;
  open: boolean;
  onClose: () => void;
}

const menuItems: MenuItem[] = [
  {
    title: "Dashboard",
    path: "/",
    icon: <DashboardIcon />,
  },
  {
    title: "Prompts",
    path: "/prompts",
    icon: <ChatBubbleOutlineIcon />,
  },
  {
    title: "Prompt Tests",
    path: "/datasets",
    icon: <StorageIcon />,
  },
  {
    title: "Providers",
    path: "/providers",
    icon: <CloudIcon />,
  },
  {
    title: "Evaluations",
    path: "/evaluations",
    icon: <AssessmentIcon />,
  },
];

const Drawer: React.FC<DrawerProps> = ({ drawerWidth, open, onClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    {},
  );

  const isDarkMode = theme.palette.mode === "dark";

  const handleGroupToggle = (title: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const drawer = (
    <>
      <DrawerHeader>
        <IconButton onClick={onClose}>
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />

      <Box sx={{ overflow: "auto", p: 2 }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);

          return (
            <ListItem key={item.title} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={RouterLink}
                to={item.path || ""}
                sx={{
                  borderRadius: 1,
                  bgcolor: active
                    ? isDarkMode
                      ? "primary.dark"
                      : "primary.main"
                    : "transparent",
                  color: active ? "#ffffff" : "text.primary",
                  boxShadow:
                    active && isDarkMode
                      ? "0 0 10px rgba(0, 0, 0, 0.3)"
                      : "none",
                  "&:hover": {
                    bgcolor: active
                      ? isDarkMode
                        ? "primary.main"
                        : "primary.dark"
                      : "action.hover",
                  },
                }}
              >
                <Tooltip title={item.title}>
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: active ? "#ffffff" : "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </Tooltip>
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </Box>
    </>
  );

  return (
    <MuiDrawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          border: "none",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {drawer}
    </MuiDrawer>
  );
};

export default Drawer;
